import * as _ from 'lodash';
import { useState } from 'react';
import type { ReactNode, VoidFunctionComponent } from 'react';

import { useAsyncStatus, useOnMount } from '../../hooks';
import * as api from '../../redux/api';
import { Link } from '../generic';
import Button from '../generic/Button';
import Card from '../generic/Card/Card';
import Flex from '../generic/Flex';
import List from '../generic/List';
import Loader from '../generic/Loader';
import type { FailedFetchResponse } from '../../helpers/errors';

type ContribCountResponse = {
  snapshot: string;
  activeContributorCountLast90days: number;
  activeContributorCountLast365days: number;
};

const ContributorsCard: VoidFunctionComponent = () => {
  const [isCopied, setIsCopied] = useState(false);
  const [trackedContribCount90, setContribCount90] = useState<number | null>(null);
  const [trackedContribCount365, setContribCount365] = useState<number | null>(null);
  const [trackedContribCountSnapshotDate, setContribCountDate] = useState('');
  const { status, onError, onPending, onSuccess } = useAsyncStatus();

  useOnMount(async () => {
    onPending();
    try {
      const res = await api.get<ContribCountResponse>('/api/contributors/latest-snapshot-count');
      if (res.data) {
        setContribCount90(res.data.activeContributorCountLast90days);
        setContribCount365(res.data.activeContributorCountLast365days);
        setContribCountDate(res.data.snapshot);
        onSuccess();
      }
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  });

  const handleCopyToClipboard = async (): Promise<void> => {
    const contributorInfo = [
      `Last snapshot: ${trackedContribCountSnapshotDate || 'n/a'}`,
      `Count (Last 90 days): ${_.isNumber(trackedContribCount90) ? trackedContribCount90 : 'n/a'}`,
      `Count (Last 365 days): ${_.isNumber(trackedContribCount365) ? trackedContribCount365 : 'n/a'}`,
    ].join('\n');

    await navigator.clipboard.writeText(contributorInfo);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  let content: ReactNode;
  if (status.loading) {
    content = <Loader />;
  } else if (!trackedContribCountSnapshotDate) {
    content = (
      <div className="flex-table text-center margin-top-lg">
        No snapshot data available.
      </div>
    );
  } else {
    content = (
      <>
        <List isBorderless>
          <List.Body hasPadding={false}>
            <List.Row
              hasTightPadding
              hasTightVerticalPadding
              left={<b>Last snapshot</b>}
              right={trackedContribCountSnapshotDate || 'n/a'}
            />
            <List.Row
              hasTightPadding
              hasTightVerticalPadding
              left={<b>Count (Last 90 days)</b>}
              right={_.isNumber(trackedContribCount90) ? trackedContribCount90 : 'n/a'}
            />
            <List.Row
              hasTightPadding
              hasTightVerticalPadding
              left={<b>Count (Last 365 days)</b>}
              right={_.isNumber(trackedContribCount365) ? trackedContribCount365 : 'n/a'}
            />
          </List.Body>
        </List>
        <p>Note: Snapshots are generated every Sunday morning.</p>
      </>
    );
  }

  return (
    <Card isFullWidth padding="medium">
      <Card.Header>
        <Flex direction="row" align="center" justify="space-between">
          <div>Tracked Contributors</div>
          <Button onClick={handleCopyToClipboard} size="micro" type="outline">
            {isCopied ? 'Copied!' : 'Copy'}
          </Button>
        </Flex>
      </Card.Header>
      <Card.Content>
        {content}
        <div>
          <Link to="/api/contributors/count" openInNewTab>
            Click here to get the latest (as of now) contributor count for the last 365 days
          </Link>
        </div>
      </Card.Content>
    </Card>
  );
};

export default ContributorsCard;
